import React from "react";
import { StatBadge } from "./StatBadge";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";

interface ServiceOrdersChartProps {
  serviceOrdersData: any;
  timePeriod: string;
  onTimePeriodChange: (period: string) => void;
}

export const ServiceOrdersChart: React.FC<ServiceOrdersChartProps> = ({
  serviceOrdersData,
  timePeriod,
  onTimePeriodChange,
}) => {
  const kpiMetrics = [
    {
      title: "Created",
      count: serviceOrdersData?.result?.serviceOrders?.created,
    },
    {
      title: "Overdue",
      count: serviceOrdersData?.result?.serviceOrders?.overdue,
    },
    {
      title: "Assigned",
      count: serviceOrdersData?.result?.serviceOrders?.assigned,
    },
    {
      title: "Completed",
      count: serviceOrdersData?.result?.serviceOrders?.completed,
    },
  ];

  const topSOPs = serviceOrdersData?.result?.topSopsUsed?.map((sop, index) => {
    const colors = [
      "text-blue-600",
      "text-green-600",
      "text-orange-600",
      "text-purple-600",
      "text-red-600",
      "text-indigo-600",
    ];
    return {
      name: sop.name,
      serviceNo: sop.serviceNo,
      uses: sop.usageCount,
      trending: "up",
      color: colors[index % colors.length],
    };
  });

  const priorityData = [
    {
      name: "Critical",
      percentage: serviceOrdersData?.result?.priorityDistribution?.critical,
      color: "bg-red-500",
    },
    {
      name: "High",
      percentage: serviceOrdersData?.result?.priorityDistribution?.high,
      color: "bg-orange-500",
    },
    {
      name: "Medium",
      percentage: serviceOrdersData?.result?.priorityDistribution?.medium,
      color: "bg-blue-500",
    },
    {
      name: "Low",
      percentage: serviceOrdersData?.result?.priorityDistribution?.low,
      color: "bg-green-500",
    },
  ];

  const handleTimePeriodChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onTimePeriodChange(e.target.value);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="h-1" />
      <div className="p-2">
        <div className="mb-2 flex justify-between items-start pb-4">
          <div>
            <h2 className="card-heading-text">Service Orders</h2>
            <p className="card-subheading-text">
              Real-time tracking of service order lifecycle
            </p>
          </div>

          <select
            value={timePeriod}
            onChange={handleTimePeriodChange}
            className="px-2 py-1 border w-[50%] border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
          >
            <option value="30">Last 30 days</option>
            <option value="60">Last 60 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>

        <div className="grid grid-cols-4 gap-1.5 mb-3">
          {kpiMetrics.map((metric, index) => (
            // <StatBadge key={index} {...metric} />
            <QuickStatsCard {...metric} />
          ))}
        </div>

        <div className="mb-3">
          <h3 className="text-xs font-semibold text-gray-700 mb-1.5">
            Top SOPs Used
          </h3>
          <div className="space-y-1">
            {topSOPs?.map((sop, index) => (
              <div
                key={index}
                className="flex items-center justify-between py-0.5"
              >
                <span className="text-xs text-gray-700">{sop.name}</span>
                <div className="flex items-center gap-1.5">
                  <span className="text-xs font-semibold text-gray-900">
                    {sop.uses}
                  </span>
                  <span className={`text-xs ${sop.color}`}>
                    {sop.trending === "up" ? "↗" : "↘"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-xs font-semibold text-gray-700 mb-1.5">
            Priority Distribution
          </h3>
          <div className="space-y-1">
            {priorityData.map((priority, index) => (
              <div key={index} className="flex items-center gap-2">
                <div className="w-10 text-xs text-gray-600">
                  {priority.name}
                </div>
                <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full ${priority.color} transition-all duration-500`}
                    style={{ width: `${priority.percentage}%` }}
                  />
                </div>
                <div className="text-xs font-semibold text-gray-900 w-6">
                  {priority.percentage}%
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
